import { getShareImageUrl } from '~/utils/proxy'

/**
 * Title, description, share card and structured data for one shop detail
 * page (release, merch, artist, plugin, genre).
 *
 * Link previews — WhatsApp, Discord, Telegram, Facebook, X, iMessage — are
 * built from the server-rendered HTML; none of those crawlers runs our JS.
 * So the page has to load its record with an awaited useFetch and hand it
 * here, not fetch it in onMounted: a release shared from the shop used to
 * preview as just "Psyfeature", no cover, no artist.
 *
 * The shop layout sets the storefront defaults; these tags use the same
 * property/name keys and register later, so they replace them.
 */
export interface ShareMeta {
    /** What is shared, e.g. "LEGEND – Cirillo JR". og:title as is; <title> gets the shop name from app.vue's titleTemplate. */
    title: string
    description?: string
    /** Stored image URL (S3, relative, or a public print-provider URL — then set imagePublic). */
    image?: string | null
    imagePublic?: boolean
    imageAlt?: string
    /** og:type: product, music.album, music.song, profile, website. */
    type?: string
    /** Extra og/product/music tags. */
    meta?: Array<Record<string, string>>
    jsonLd?: Record<string, unknown> | null
}

const OG_LOCALE: Record<string, string> = { en: 'en_US', de: 'de_DE', pt: 'pt_BR', es: 'es_ES' }
const SHARE_IMAGE_WIDTH = 1200

/** Collapsed whitespace, cut on a word near `max`. */
export function shareExcerpt(text: unknown, max = 160): string {
    const flat = String(text || '').replace(/\s+/g, ' ').trim()
    if (flat.length <= max) return flat
    const cut = flat.slice(0, max - 1)
    return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), max - 30))}…`
}

const LOCALE_TAG: Record<string, string> = { en: 'en-US', de: 'de-DE', pt: 'pt-BR', es: 'es-ES' }

/**
 * Prices and dates for share texts, in the page's language. Dates in UTC:
 * release dates are stored as UTC midnight, and the server-rendered text is
 * the one crawlers keep — a server in another zone must not shift the day.
 */
export function useShareFormat() {
    const { locale } = useI18n()
    const tag = () => LOCALE_TAG[locale.value] || 'en-US'
    return {
        price: (v: unknown) => new Intl.NumberFormat(tag(), { style: 'currency', currency: 'EUR' }).format(Number(v) || 0),
        date: (v: unknown) => {
            const d = new Date(v as any)
            return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat(tag(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d)
        }
    }
}

/**
 * `localeAlternates: false` for pages that exist in one language only: the
 * shoplink catch-all would read /de/<shortId> as the shortId "de".
 */
export function useShareMeta(source: () => ShareMeta | null, opts: { notFoundTitle?: string; localeAlternates?: boolean } = {}) {
    // Origin from the request, path from the (reactive) route: query strings
    // (utm_*, ?page=2) must not fork the canonical URL.
    const origin = useRequestURL().origin
    const route = useRoute()
    const { appName } = useTenantTheme()
    const { locale } = useI18n()
    const switchLocalePath = useSwitchLocalePath()

    useHead(() => {
        const s = source()
        if (!s) {
            // Unknown record: tell crawlers instead of indexing a "not found" body.
            return {
                title: opts.notFoundTitle || appName.value,
                meta: [{ name: 'robots', content: 'noindex' }]
            }
        }
        const url = origin + route.path
        const image = !s.image ? '' : s.imagePublic ? s.image : getShareImageUrl(s.image, origin, { width: SHARE_IMAGE_WIDTH })
        const site = appName.value
        const meta: Array<Record<string, string>> = [
            { property: 'og:type', content: s.type || 'website' },
            { property: 'og:title', content: s.title },
            { property: 'og:url', content: url },
            { property: 'og:locale', content: OG_LOCALE[locale.value] || 'en_US' },
            { name: 'twitter:title', content: s.title }
        ]
        if (site) meta.push({ property: 'og:site_name', content: site })
        if (s.description) {
            meta.push(
                { name: 'description', content: s.description },
                { property: 'og:description', content: s.description },
                { name: 'twitter:description', content: s.description }
            )
        }
        if (image) {
            meta.push(
                { property: 'og:image', content: image },
                { property: 'og:image:alt', content: s.imageAlt || s.title },
                { name: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:image', content: image }
            )
        }
        meta.push(...(s.meta || []))

        const jsonLd = s.jsonLd
            ? { '@context': 'https://schema.org', ...s.jsonLd, url, ...(image ? { image: [image] } : {}) }
            : null
        // The same record in every shop language, so search engines treat
        // /de/releases/x and /releases/x as translations of one page rather
        // than duplicates. English (unprefixed) is the x-default.
        // Path only, like the canonical: switchLocalePath carries the query
        // (?page=2), which would make the alternates disagree with it.
        const alternates = opts.localeAlternates === false ? [] : Object.keys(OG_LOCALE).map(code => ({
            rel: 'alternate',
            hreflang: code,
            href: origin + (switchLocalePath(code as any) || route.path).split(/[?#]/)[0]
        }))
        const enHref = alternates.find(a => a.hreflang === 'en')?.href
        if (enHref) alternates.push({ rel: 'alternate', hreflang: 'x-default', href: enHref })
        return {
            // app.vue's titleTemplate appends " - <shop name>" to <title>.
            title: s.title,
            meta,
            link: [{ rel: 'canonical', href: url }, ...alternates],
            script: jsonLd
                ? [{
                    type: 'application/ld+json',
                    key: 'share-jsonld',
                    // `<` escaped so a description containing a closing script tag cannot break out.
                    innerHTML: JSON.stringify(jsonLd).replace(/</g, '\\u003c')
                }]
                : []
        }
    })
}
